import { useState } from "react";
import { useVault } from "../hooks/useVault.jsx";
import { useToast } from "../hooks/useToast.jsx";
import { EmptyState } from "../components/EmptyState.jsx";
import { SparkleIcon, SearchIcon } from "../components/Icons.jsx";
import * as AI from "../lib/ai.js";

const SUGGESTIONS = [
  "What bills are coming up?",
  "What did I write about work this month?",
  "Summarise my journal for the last few weeks",
  "What goals am I behind on?",
];

export function AskAIView({ onOpenEntry }) {
  const { entries, setting, SETTING_IDS } = useVault();
  const toast = useToast();

  const settings = setting(SETTING_IDS.preferences, {});
  const ready = AI.isConfigured(settings);

  const [question, setQuestion] = useState("");
  const [scope, setScope] = useState("all");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (q = question) => {
    if (!q.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const pool = scope === "all" ? entries : entries.filter((e) => e.category === scope);
      setResult(await AI.askVault(settings, q, pool));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!ready) {
    return (
      <EmptyState icon={SparkleIcon} title="AI isn't switched on yet">
        Open Settings to add an Anthropic API key, or point AIDIA at the backend so the
        key stays on the server. Everything else works without it.
      </EmptyState>
    );
  }

  return (
    <div className="col" style={{ gap: "var(--s-5)", maxWidth: 840 }}>
      <div className="panel">
        <div className="panel-title">Ask across your vault</div>
        <div className="row" style={{ gap: "var(--s-2)" }}>
          <div className="search-box grow">
            <SearchIcon size={15} />
            <input type="text" value={question} placeholder="Ask a question about your notes…"
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()} />
          </div>
          <select value={scope} onChange={(e) => setScope(e.target.value)} style={{ width: 150 }}>
            <option value="all">All categories</option>
            <option value="diary">Diary</option>
            <option value="journal">Journal</option>
            <option value="voice">Voice Notes</option>
            <option value="password">Passwords</option>
          </select>
          <button className="btn btn-primary" onClick={() => run()} disabled={busy || !question.trim()}>
            {busy ? <span className="spinner" /> : <SparkleIcon size={15} />} Ask
          </button>
        </div>

        <div className="ai-suggestions" style={{ marginTop: "var(--s-3)" }}>
          {SUGGESTIONS.map((s) => (
            <button key={s} className="chip" onClick={() => { setQuestion(s); run(s); }}>{s}</button>
          ))}
        </div>

        <p className="hint-text" style={{ marginTop: "var(--s-3)" }}>
          The notes in scope are decrypted and sent to Anthropic to answer.
          Stored password values are always withheld.
        </p>
      </div>

      {busy && (
        <div className="panel row" style={{ gap: "var(--s-3)" }}>
          <span className="spinner" /> <span className="secondary">Reading your entries…</span>
        </div>
      )}

      {result && (
        <>
          <div className="ai-answer">
            {renderWithCitations(result.answer, result.used, onOpenEntry)}
          </div>
          <div className="small muted">
            Answered from {result.used.length} {result.used.length === 1 ? "entry" : "entries"}.
          </div>
        </>
      )}
    </div>
  );
}

/** Turn "[Entry 3]" into a button that opens that entry. */
function renderWithCitations(text, used, onOpenEntry) {
  const parts = String(text).split(/(\[Entry \d+\])/g);
  return parts.map((part, i) => {
    const m = /^\[Entry (\d+)\]$/.exec(part);
    if (!m) return <span key={i}>{part}</span>;
    const entry = used[Number(m[1]) - 1];
    if (!entry) return <span key={i}>{part}</span>;
    return (
      <button key={i} className="cite" title={entry.title || "Untitled"}
        onClick={() => onOpenEntry(entry)}>
        {part}
      </button>
    );
  });
}
